import React from "react";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../common";

/**
 * Shared chrome for every Settings section, so seven separately-built panels
 * read as one product.
 *
 * Three things it provides that the old stacked-cards page did not:
 *
 * 1. **A real page header per section** instead of being the fourth box down a
 *    scroll — tinted icon tile, title, description, and a slot for a status
 *    badge or a primary action.
 * 2. **Inline errors.** The old page hoisted every failure into one banner at
 *    the top; on a 677-line page that put the message several hundred pixels
 *    away from the control that produced it.
 * 3. **A sticky save bar that appears when the form is dirty.** A lone Save
 *    button at the bottom of a twenty-field form is invisible from wherever you
 *    are typing. The bar also holds for a moment on "Saved", because a
 *    confirmation that renders where nobody is looking is not a confirmation.
 */

// Tinted icon tiles. Alpha modifiers are safe on all of these: `primary` and
// `glow` are channel-backed brand tokens, `success` is a plain hex, and
// `amber-500` is a palette colour. (The five *theme* tokens are the ones where
// an alpha modifier silently emits no rule -- see index.css.)
const TONES = {
  primary: "bg-primary/15 text-primary",
  glow:    "bg-glow/15 text-glow",
  success: "bg-success/15 text-success",
  amber:   "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

/** An eyebrow-labelled group of fields. Turns a wall of twenty inputs into
 *  named clusters. Siblings are separated by a hairline via the parent's
 *  `divide-y`, so the first group needs no rule above it. */
export function FieldGroup({ label, hint, children, className = "" }) {
  return (
    <section className={`py-6 first:pt-5 ${className}`}>
      {label && (
        <h3 className="text-xs uppercase tracking-[0.2em] text-muted">{label}</h3>
      )}
      {hint && <p className="text-xs text-muted mt-1.5 max-w-prose">{hint}</p>}
      <div className={label || hint ? "mt-4 space-y-3" : "space-y-3"}>{children}</div>
    </section>
  );
}

/** Wraps FieldGroups so they get their dividers. */
export function FieldGroups({ children, className = "" }) {
  return <div className={`divide-y divide-border ${className}`}>{children}</div>;
}

export default function SectionShell({
  icon: Icon, tone = "primary", title, desc, aside, error, children,
  dirty = false, saving = false, saved = false, onSave, onDiscard,
}) {
  const { t } = useTranslation();
  const showBar = Boolean(onSave) && (dirty || saved);

  return (
    <section className="min-w-0">
      <header className="flex items-start gap-3.5 pb-5 border-b border-border">
        <span aria-hidden="true"
          className={`w-11 h-11 rounded-sm shrink-0 flex items-center justify-center ${TONES[tone] || TONES.primary}`}>
          <Icon size={19} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
          {desc && <p className="text-sm text-muted mt-0.5">{desc}</p>}
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>

      {error ? (
        <div role="alert" data-testid="section-error"
          className="flex items-start gap-2 mt-5 px-3.5 py-3 rounded-sm border border-danger/40 bg-danger/10 text-sm">
          <AlertCircle size={16} className="text-danger shrink-0 mt-0.5" />
          <span className="min-w-0">{error}</span>
        </div>
      ) : null}

      {children}

      {/* Sticky at bottom-20 on mobile, not bottom-0: Layout's mobile tab bar is
          `fixed bottom-0 mx-3 mb-3`, so a bar at 0 would sit underneath it. */}
      {Boolean(onSave) && (
        <div aria-hidden={!showBar}
          className={`sticky bottom-20 lg:bottom-3 z-30 mt-6 transition-all duration-200 ${
            showBar ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}>
          <div data-testid="section-save-bar"
            className="flex items-center gap-3 px-4 py-2.5 rounded-sm border border-border bg-frost backdrop-blur-xl shadow-2xl">
            {saved && !dirty ? (
              <>
                <Check size={15} className="text-success shrink-0" />
                <span className="text-sm text-muted flex-1 min-w-0 truncate">{t("settings.savedFlash")}</span>
              </>
            ) : (
              <>
                <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                <span className="text-sm text-muted flex-1 min-w-0 truncate">{t("settings.unsaved")}</span>
                {onDiscard && (
                  <button type="button" onClick={onDiscard} data-testid="section-discard-btn"
                    className="px-2.5 py-1.5 rounded-sm text-sm text-muted hover:text-txt hover:bg-surface transition-colors">
                    {t("settings.discard")}
                  </button>
                )}
                <Button onClick={onSave} disabled={saving} data-testid="section-save-btn">
                  {saving
                    ? <><Loader2 size={15} className="animate-spin" /> {t("settings.saving")}</>
                    : t("common.save")}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
